import React, { forwardRef, useImperativeHandle, useRef } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import NoProductImage from "../../../../../images/no-product-image-available.svg";
import { PolylineOutlined } from "@mui/icons-material";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import CatalogedProduct from "shared-ui/src/types/ecommerce/CatalogedProduct";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import { PRODUCT_IMAGE_SIZE } from "shared-ui/src/types/ecommerce/constants";
import { getFormattedPrice } from "shared-ui/src/components/ecommerce/helpers/getValidPrice";

const getProductUrl = (
  shiComBaseUrl: string,
  product: CatalogedProduct,
): string => {
  const id = product.productDescriptor;
  return `${shiComBaseUrl}/product/${id}`;
};

export interface ProductListHandle {
  focusFirstItem: () => void;
}

const ProductList = forwardRef<
  ProductListHandle,
  {
    relatedProducts?: CatalogedProduct[];
    onAddToCart: (product: CatalogedProduct) => void;
  }
>(({ relatedProducts, onAddToCart }, ref) => {
  const { hasPermission } = useClaims();
  const shiComBaseUrl = getShiComBaseUrl();
  const listRef = useRef<HTMLUListElement>(null);

  const canViewCart = hasPermission(ShiOnePermission.shoppingCartView);
  // Matches the add endpoint's ShoppingCartSave gate (EcommerceShoppingCartController "add").
  const canSaveToCart = hasPermission(ShiOnePermission.shoppingCartSave);

  useImperativeHandle(ref, () => ({
    focusFirstItem: () => {
      const firstFocusable = listRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) as HTMLElement | null;
      firstFocusable?.focus();
    },
  }));

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const focusableElements =
      listRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) || [];
    const elements = Array.from(focusableElements);
    const currentIndex = elements.indexOf(
      document.activeElement as HTMLElement,
    );

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (currentIndex === -1) {
        elements[0]?.focus();
      } else {
        const nextIndex = (currentIndex + 1) % elements.length;
        elements[nextIndex]?.focus();
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (currentIndex === -1) {
        elements[elements.length - 1]?.focus();
      } else if (currentIndex === 0) {
        // If we are at the first element and press ArrowUp, we should let the event bubble up
        // to possibly focus the search input again, but our current structure might not
        // easily support that without a callback. For now, just wrap around or stay.
        const prevIndex =
          (currentIndex - 1 + elements.length) % elements.length;
        elements[prevIndex]?.focus();
      } else {
        const prevIndex =
          (currentIndex - 1 + elements.length) % elements.length;
        elements[prevIndex]?.focus();
      }
    } else if (event.key === "Enter") {
      // If an element is focused, let the default behavior happen (click the link or button)
    } else if (event.key === "Escape") {
      // Let it bubble
    }
  };

  const handleAddToCart = (
    event: React.MouseEvent,
    product: CatalogedProduct,
  ) => {
    event.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  if (!relatedProducts?.length) {
    return null;
  }

  return (
    <Paper
      id="product-list-results"
      role="listbox"
      aria-label="Product search results"
      elevation={8}
      sx={{
        mt: 0.5,
        maxHeight: "80vh",
        width: { xs: "100%", md: "972px" },
        overflowY: "auto",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.default",
        position: { xs: "fixed", md: "absolute" },
        left: { xs: 0, md: "auto" },
        right: { xs: 0, md: "auto" },
        zIndex: 1300,
        // Ensure it doesn't cover too much on mobile
        top: { xs: "56px", md: "auto" },
        bottom: { xs: 0, md: "auto" },
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <PolylineOutlined fontSize="large" sx={{ color: "text.secondary" }} />
        <Typography variant="h6">Related Products</Typography>
      </Box>
      <List ref={listRef} sx={{ p: 0 }} onKeyDown={handleKeyDown}>
        {relatedProducts.map((product) => {
          // Price visibility keeps the original View gate; only adding needs Save.
          const isPriceAvailable =
            canViewCart && product.canAddToCart === true;
          const canAddToCart = isPriceAvailable && canSaveToCart;
          const imageUrl = product.imageUrl?.trim();
          const hasImage = Boolean(imageUrl);
          return (
            <ListItem
              key={product.id}
              role="option"
              aria-selected={false} // Would need a state to track "selected" but focus is usually enough for simple lists
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&:focus-within": {
                  bgcolor: "action.selected",
                },
                py: 2,
                px: { xs: 2, md: 3 },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: `${PRODUCT_IMAGE_SIZE}px 1fr`,
                    sm: `${PRODUCT_IMAGE_SIZE}px minmax(200px, auto) 1fr auto`,
                  },
                  gridTemplateRows: { xs: "auto auto auto", sm: "auto auto" },
                  gap: 2,
                  width: "100%",
                  alignItems: "center",
                }}
              >
                <Box sx={{ gridColumn: 1, gridRow: "1 / span 2" }}>
                  <Link
                    href={getProductUrl(shiComBaseUrl, product)}
                    sx={{
                      display: "block",
                      lineHeight: 0,
                      borderRadius: 1,
                      p: 0.5,
                      m: -0.5,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                      "&:focus": {
                        outline: "none",
                        bgcolor: "action.hover",
                        boxShadow: (theme) =>
                          `0 0 0 3px ${theme.palette.primary.main}80`,
                      },
                      "&:focus-visible": {
                        outline: "none",
                        bgcolor: "action.hover",
                        boxShadow: (theme) =>
                          `0 0 0 3px ${theme.palette.primary.main}80`,
                      },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Avatar
                      variant="square"
                      src={hasImage ? imageUrl : NoProductImage}
                      alt={hasImage ? (product.name ?? undefined) : "No product image"}
                      sx={{
                        width: PRODUCT_IMAGE_SIZE,
                        height: PRODUCT_IMAGE_SIZE,
                        "& img": {
                          objectFit: "contain",
                          objectPosition: "center",
                        },
                      }}
                    />
                  </Link>
                </Box>

                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 1,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  <Link
                    href={getProductUrl(shiComBaseUrl, product)}
                    underline="none"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: "text.primary",
                      borderRadius: 1,
                      px: 0.5,
                      py: 0.1,
                      m: -0.5,
                      maxWidth: { xs: "100%", sm: "300px", md: "400px" },
                      "&:hover": {
                        color: "primary.main",
                        bgcolor: "action.hover",
                      },
                      "&:focus": {
                        outline: "none",
                        bgcolor: "action.hover",
                        boxShadow: (theme) =>
                          `0 0 0 3px ${theme.palette.primary.main}80`,
                      },
                      "&:focus-visible": {
                        outline: "none",
                        bgcolor: "action.hover",
                        boxShadow: (theme) =>
                          `0 0 0 3px ${theme.palette.primary.main}80`,
                      },
                    }}
                  >
                    <ListItemText
                      primary={product.name}
                      slotProps={{
                        primary: {
                          title: product.name ?? undefined,
                          variant: "body1",
                          noWrap: true,
                        },
                      }}
                      sx={{ my: 0 }}
                    />
                  </Link>
                </Box>

                <Link
                  href={getProductUrl(shiComBaseUrl, product)}
                  underline="hover"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    gridColumn: 2,
                    gridRow: 2,
                    color: "text.secondary",
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.1,
                    m: -0.5,
                    display: "inline-block",
                    width: "fit-content",
                    mt: -1.75, // Adjust to pull it closer to the name
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: "action.hover",
                    },
                    "&:focus": {
                      outline: "none",
                      bgcolor: "action.hover",
                      boxShadow: (theme) =>
                        `0 0 0 3px ${theme.palette.primary.main}80`,
                    },
                    "&:focus-visible": {
                      outline: "none",
                      bgcolor: "action.hover",
                      boxShadow: (theme) =>
                        `0 0 0 3px ${theme.palette.primary.main}80`,
                    },
                  }}
                >
                  <Typography variant="body2">
                    {product.manufacturerPartNumber}
                  </Typography>
                </Link>

                <Box
                  sx={{
                    gridColumn: 3,
                    gridRow: 1,
                    display: { xs: "none", sm: "block" },
                    height: "1px",
                    bgcolor: "divider",
                    opacity: 0.6,
                    mx: 2,
                  }}
                />

                <Box
                  sx={{
                    gridColumn: { xs: 2, sm: 4 },
                    gridRow: { xs: 3, sm: 1 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                    gap: 1.5,
                    minWidth: 0,
                    mt: { xs: 0.5, sm: 0 },
                  }}
                >
                  {!isPriceAvailable ? (
                    <Typography
                      variant="body2"
                      color="warning.light"
                      sx={{
                        textAlign: { xs: "left", sm: "right" },
                        whiteSpace: { xs: "normal", sm: "nowrap" },
                        mr: { xs: 0, sm: canSaveToCart ? 7 : 0 },
                      }}
                    >
                      Please call for pricing and availability.
                    </Typography>
                  ) : (
                    <Typography noWrap variant="body2" color="warning.main">
                      {getFormattedPrice(product.price)}
                    </Typography>
                  )}
                  {canAddToCart && (
                    <IconButton
                      size="small"
                      aria-label={`Add ${product.name} to cart`}
                      onClick={(e) => handleAddToCart(e, product)}
                      sx={{
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                        width: 44,
                        height: 44,
                        p: 0, // Ensure no extra padding pushes the icon
                      }}
                    >
                      <ShoppingCartIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </ListItem>
          );
      })}
    </List>
    </Paper>
  );
});

export default ProductList;
